import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  TextInput,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from './ThemedText';

interface DropdownItem {
  id: string;
  name: string;
  subtitle?: string;
}

interface SearchableDropdownProps {
  data: string[] | DropdownItem[];
  value: string;
  onSelect: (item: string | DropdownItem) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  data,
  value,
  onSelect,
  placeholder = 'Search...',
  label,
  required = false,
  disabled = false,
}) => {
  const { theme } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState<(string | DropdownItem)[]>(data);

  const closeModal = () => {
    console.log('🔴 [SearchableDropdown] Closing modal');
    setShowModal(false);
  };

  const openModal = () => {
    console.log('🟢 [SearchableDropdown] Opening modal');
    setShowModal(true);
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredData(data);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = data.filter((item) => {
        if (typeof item === 'string') {
          return item.toLowerCase().includes(query);
        } else {
          return (
            item.name.toLowerCase().includes(query) ||
            item.subtitle?.toLowerCase().includes(query)
          );
        }
      });
      setFilteredData(filtered);
    }
  }, [searchQuery, data]);

  const handleSelect = (item: string | DropdownItem) => {
    console.log('✅ [SearchableDropdown] Item selected:', item);
    onSelect(item);
    closeModal();
    setSearchQuery('');
  };

  const getDisplayValue = () => {
    if (!value) return '';
    return value;
  };

  return (
    <View style={styles.container}>
      {label && (
        <ThemedText style={[styles.label, { color: theme.text }]}>
          {label}
          {required && <ThemedText style={styles.required}>*</ThemedText>}
        </ThemedText>
      )}
      
      <TouchableOpacity
        style={[
          styles.input,
          { backgroundColor: theme.card, borderColor: theme.border },
          disabled && { opacity: 0.5 },
        ]}
        onPress={() => {
          if (!disabled) {
            console.log('📱 [SearchableDropdown] Input pressed');
            openModal();
          }
        }}
        disabled={disabled}
      >
        <ThemedText
          style={[
            styles.inputText,
            { color: value ? theme.text : theme.textTertiary },
          ]}
          numberOfLines={1}
        >
          {getDisplayValue() || placeholder}
        </ThemedText>
        <Ionicons
          name="chevron-down"
          size={20}
          color={theme.textSecondary}
        />
      </TouchableOpacity>

      <Modal
        visible={showModal}
        transparent
        animationType="slide"
        onRequestClose={() => {
          console.log('❌ [SearchableDropdown] Modal onRequestClose');
          closeModal();
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1 }}
        >
          <TouchableOpacity 
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => {
              console.log('🌑 [SearchableDropdown] Overlay pressed - closing');
              closeModal();
            }}
          >
            <TouchableOpacity
              activeOpacity={1}
              style={[styles.modalContent, { backgroundColor: theme.background }]}
              onPress={(e) => {
                console.log('🛑 [SearchableDropdown] Content pressed - stopping propagation');
                e.stopPropagation();
              }}
            >
            <View style={[styles.modalHeader, { borderBottomColor: theme.border }]}>
              <ThemedText style={[styles.modalTitle, { color: theme.text }]}>
                {label || 'Select'}
              </ThemedText>
              <TouchableOpacity onPress={() => {
                console.log('✖️ [SearchableDropdown] Close button pressed');
                closeModal();
              }}>
                <Ionicons name="close" size={24} color={theme.text} />
              </TouchableOpacity>
            </View>

            <View style={[styles.searchContainer, { borderBottomColor: theme.border }]}>
              <Ionicons name="search" size={20} color={theme.textSecondary} />
              <TextInput
                style={[styles.searchInput, { color: theme.text }]}
                value={searchQuery}
                onChangeText={(text) => {
                  console.log('🔍 [SearchableDropdown] Search query:', text);
                  setSearchQuery(text);
                }}
                placeholder="Search..."
                placeholderTextColor={theme.textTertiary}
                autoFocus={Platform.OS !== 'web'}
                onFocus={() => console.log('⌨️ [SearchableDropdown] Search input focused')}
                onBlur={() => console.log('💨 [SearchableDropdown] Search input blurred')}
                {...(Platform.OS === 'web' ? {
                  onClick: (e: any) => {
                    console.log('🖱️ [SearchableDropdown] Search input clicked');
                    e.stopPropagation();
                  },
                } : {})}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery('')}>
                  <Ionicons name="close-circle" size={20} color={theme.textSecondary} />
                </TouchableOpacity>
              )}
            </View>

            <FlatList
              data={filteredData}
              keyExtractor={(item, index) =>
                typeof item === 'string' ? item : item.id || index.toString()
              }
              renderItem={({ item }) => {
                const isString = typeof item === 'string';
                const itemName = isString ? item : item.name;
                const isSelected = value === itemName || value === (isString ? '' : item.id);

                return (
                  <TouchableOpacity
                    style={[
                      styles.listItem,
                      { borderBottomColor: theme.border },
                      isSelected && { backgroundColor: theme.accent + '20' },
                    ]}
                    onPress={() => handleSelect(item)}
                  >
                    <View style={styles.listItemContent}>
                      <ThemedText style={[styles.listItemText, { color: theme.text }]}>
                        {itemName}
                      </ThemedText>
                      {!isString && item.subtitle && (
                        <ThemedText style={[styles.listItemSubtitle, { color: theme.textSecondary }]}>
                          {item.subtitle}
                        </ThemedText>
                      )}
                    </View>
                    {isSelected && (
                      <Ionicons name="checkmark" size={20} color={theme.accent} />
                    )}
                  </TouchableOpacity>
                );
              }}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
                    No results found
                  </ThemedText>
                </View>
              }
              style={styles.list}
            />
          </TouchableOpacity>
        </TouchableOpacity>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  required: {
    color: '#FF4444',
  },
  input: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    minHeight: 48,
  },
  inputText: {
    flex: 1,
    fontSize: 14,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-start',
    paddingTop: Platform.OS === 'ios' ? 80 : 40,
    paddingHorizontal: 20,
  },
  modalContent: {
    borderRadius: 12,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 8,
    borderBottomWidth: 1,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    padding: 0,
  },
  list: {
    maxHeight: 400,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
  },
  listItemContent: {
    flex: 1,
  },
  listItemText: {
    fontSize: 15,
  },
  listItemSubtitle: {
    fontSize: 13,
    marginTop: 2,
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
  },
});

export default SearchableDropdown;
